/**
 * T020 - US1: Provider Details Resources Tab Component
 * Feature 011 - Service Provider Profile Improvements
 *
 * Displays resources authored by the provider with conditional rendering:
 * - Sub-tabs are added on demand for each non-empty bucket (resources, photos,
 *   documents). When only one bucket has content, render it directly (no
 *   sub-tabs). When none have content, return null so the parent hides the tab.
 *
 * Feature 073 adds the `Documents` (PDF) bucket alongside resources/photos.
 */

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../shared/components/ui/tabs';
import { ProviderResourcesGrid } from './ProviderResourcesGrid';
import { ProviderDetailsMedia } from './ProviderDetailsMedia';
import { ProviderDetailsDocuments } from './ProviderDetailsDocuments';
import { useProviderResources } from '../hooks/useProviderResources';
import { SkeletonGroup } from '../../../shared/components/SkeletonLoader';
import type {
  ProviderDocument,
  ProviderMedia,
} from '../types/provider-profile.types';

interface ProviderDetailsResourcesProps {
  providerId: string;
  photos: ProviderMedia[];
  documents?: ProviderDocument[];
}

export const ProviderDetailsResources = ({
  providerId,
  photos = [],
  documents = [],
}: ProviderDetailsResourcesProps) => {
  const { data: resources = [], isLoading } = useProviderResources(providerId);

  const hasResources = resources.length > 0;
  const hasPhotos = photos.length > 0;
  const hasDocuments = documents.length > 0;

  if (!hasResources && !hasPhotos && !hasDocuments && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <div data-testid="skeleton-loader">
        <SkeletonGroup.MediaGallery />
      </div>
    );
  }

  // Build the dynamic set of sub-tabs based on which buckets have content.
  const subTabs: Array<{
    value: 'resources' | 'photos' | 'documents';
    label: string;
    content: React.ReactNode;
  }> = [];

  if (hasResources) {
    subTabs.push({
      value: 'resources',
      label: 'Resources',
      content: <ProviderResourcesGrid resources={resources} />,
    });
  }
  if (hasPhotos) {
    subTabs.push({
      value: 'photos',
      label: 'Photos',
      content: <ProviderDetailsMedia provider={{ media: photos }} />,
    });
  }
  if (hasDocuments) {
    subTabs.push({
      value: 'documents',
      label: 'Documents',
      content: <ProviderDetailsDocuments documents={documents} />,
    });
  }

  if (subTabs.length === 1) {
    return <>{subTabs[0].content}</>;
  }

  return (
    <Tabs defaultValue={subTabs[0].value} className="space-y-6">
      <TabsList>
        {subTabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {subTabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};
